/**
 * groupBrowser.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";

import MenuIcon from "@mui/icons-material/Menu";
import { IconButton, TextField } from "@mui/material";

import { GroupNavContext } from "../chat/groupNav.context";

import { Group, GroupMembership } from "../models/group";

import { groupServiceFactory } from "../services/group.service";

interface GroupBrowserComponentProps {
  accountID: string,
  memberships: GroupMembership[],
  onJoinGroup(group: Group): void
}

interface GroupBrowserComponentState {
  loading: boolean,
  searchText: string,
  groups: Group[],
  membershipMap: {[id: string]: GroupMembership}
}

export class GroupBrowserComponent extends React.Component<GroupBrowserComponentProps, GroupBrowserComponentState> {
  static contextType = GroupNavContext;
  declare context: React.ContextType<typeof GroupNavContext>;

  searchTimeout: NodeJS.Timeout | null;

  constructor(props: GroupBrowserComponentProps) {
    super(props);

    this.changedSearchText = this.changedSearchText.bind(this);
    this.searchTimeout = null;

    this.state = {
      loading: false,
      searchText: "",
      groups: [],
      membershipMap: {}
    };
  }

  async componentDidMount (): Promise<void> {
    try {
      const groupService = groupServiceFactory();
      this.setState({loading: true});
      this.buildMembershipMap();
      if (!this.props.accountID) {
        return;
      }

      const groups = await groupService.getGroups(this.props.accountID);
      this.setState({groups: groups});
    } finally {
      this.setState({loading: false});
    }
  }

  async componentDidUpdate(prevProps: GroupBrowserComponentProps): Promise<void> {
    if (this.props.memberships.length !== prevProps.memberships.length) {
      this.buildMembershipMap();
    }
  }

  buildMembershipMap(): void {
    const map: {[id: string]: GroupMembership} = {};
    for (const member of this.props.memberships) {
      map[member.groupID] = member;
    }
    this.setState({membershipMap: map});
  }

  async changedSearchText(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const newText = event.target.value as string;
    this.setState({searchText: newText});
    
    this.searchTimeout && clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(async () => {
      try {
        const groups = await groupServiceFactory().searchGroups(this.props.accountID, newText, {limit: 100});
        this.setState({groups: groups});
      } catch (err) {
        console.error("Error updating group list:", err);
      }
    }, 500);
  }

  render(): React.ReactElement<any, any> {
    return <div className="group-browser-container">
      <header className="group-header-nav attached-left-nav">
        <IconButton className="left-nav-outer-toggle" size="small"
            disableRipple disableTouchRipple
            onClick={this.context.toggleGroupList}>
          <MenuIcon />
        </IconButton>
        <h3>Browse Groups</h3>
      </header>
      <TextField size="small" variant="outlined" label="Search Groups" fullWidth
          className="group-search-field"
          value={this.state.searchText}
          onChange={this.changedSearchText} />

      {this.state.groups.map(group =>
        <GroupListItemComponent
          key={group._id}
          group={group}
          missingMembership={!this.state.membershipMap[group._id]}
          onJoinGroup={this.props.onJoinGroup} />
      )}
    </div>;
  }
}

interface Props {
  group: Group,
  missingMembership: boolean,
  onJoinGroup(group: Group): void
}
function GroupListItemComponent(props: Props): React.ReactElement<any, any> {
  return <div className="group-list-item">
    <div className="group-name">{props.group.groupname}</div>
    {props.missingMembership &&
      <IconButton aria-label="group-info-menu" aria-haspopup="true"
          className="join-btn" size="small"
          disableRipple disableTouchRipple
          onClick={() => props.onJoinGroup(props.group)}>
        <i className="fas fa-sign-in-alt" />
      </IconButton> }
    <div className="group-desc">{props.group.desc}</div>
  </div>;
}